import React, { ComponentProps, memo, ReactNode } from 'react';

import {
  ZapIcon,
  CircleAlertIcon,
  FileVolumeIcon,
  GoalIcon,
  MegaphoneIcon,
  SmartphoneIcon,
  WorkflowIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DeliveryErrorTooltip } from '@/components/atoms/DeliveryErrorTooltip/DeliveryErrorTooltip';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useBrains from '@/hooks/useBrains';
import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import { useBroadcasts } from '@/modules/broadcast/hooks/useBroadcasts';
import { useCampaigns } from '@/modules/campaigns/hooks/useCampaigns';
import { buildStoredFileUrl } from '@/modules/humanChat/hooks/useAttachment';
import { AuthorType, ConversationSource, Message as MessageType } from '@/shared/types/chat';
import { splitArray } from '@/util/util';

import { Attachment } from './Attachment/Attachment';
import Attachments from './Attachment/Attachments';
import { ImageStack } from './Attachment/ImageStack';
import { Response } from './Response/Response';

// Markdown styling for visitor message text (headings, blockquotes, links).
// Shared so the styled bubble and the floating image/file branch render identically.
const VISITOR_MARKDOWN_CLASSES =
  '[&_a]:text-primary [&_a]:underline [&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/40 [&_blockquote]:pl-3 [&>h1]:mb-2 [&>h1]:text-[32px] [&>h1]:leading-10 [&>h1]:font-semibold [&>h2]:mb-1.5 [&>h2]:text-[22px] [&>h2]:leading-7 [&>h2]:font-semibold [&>h3]:mb-1 [&>h3]:text-lg [&>h3]:leading-6 [&>h3]:font-semibold [&>h4]:mb-1 [&>h4]:text-base [&>h4]:leading-6 [&>h4]:font-semibold [&>h5]:mb-0.5 [&>h5]:text-sm [&>h5]:leading-5 [&>h5]:font-semibold [&>h6]:mb-0.5 [&>h6]:text-xs [&>h6]:leading-4 [&>h6]:font-semibold';

/**
 * Renders either an error icon or the provided content based on message failure status
 */
const MessageAvatar = ({
  showAvatar,
  message,
  children,
}: {
  showAvatar: boolean;
  message: MessageType;
  children: ReactNode;
}): React.JSX.Element | null => {
  if (!showAvatar) {
    return null;
  }

  if (!message.failed_at) {
    return <>{children}</>;
  }

  return (
    <DeliveryErrorTooltip errorCode={message.error_code}>
      <CircleAlertIcon size={24} className="text-destructive" />
    </DeliveryErrorTooltip>
  );
};

interface Props {
  message: MessageType;
  showAvatar: boolean;
  group: boolean;
  source: ConversationSource;
  handleAttachmentError?: (error?: Error) => void;
}

const Message = ({ message, showAvatar, group, source, handleAttachmentError }: Props) => {
  const { t } = useTranslation();
  const { getMember } = useMembers();
  const brainId = message.author_type === 'brain' ? message.author_id : null;
  const { brain } = useBrains(brainId);
  const broadcastId = message.author_type === 'broadcast' ? message.author_id : null;
  const { broadcast } = useBroadcasts({ broadcastId });
  const campaignId = message.author_type === 'campaign' ? message.author_id : null;
  const { campaign } = useCampaigns(campaignId);
  const { html } = useMarkdownToHtml(message.text || '');
  const authorMember = getMember(message.author_id);

  // VISITOR
  if (message.author_type === 'visitor') {
    const hasAttachments = message?.body?.attachments?.length > 0;
    const visitorAttachments = message?.body?.attachments ?? [];
    const allVisitorImages =
      hasAttachments && visitorAttachments.every((a) => a.mime_type?.includes('image'));
    const singleVisitorFile =
      visitorAttachments.length === 1 &&
      visitorAttachments[0].type === 'file' &&
      !visitorAttachments[0].mime_type?.includes('image') &&
      !visitorAttachments[0].mime_type?.includes('video') &&
      !visitorAttachments[0].mime_type?.includes('audio');

    // Images and single non-media files float freely — no bubble backdrop
    if (allVisitorImages || singleVisitorFile) {
      let attachmentChild: React.ReactNode;
      if (allVisitorImages && visitorAttachments.length > 1) {
        attachmentChild = (
          <ImageStack
            attachments={visitorAttachments}
            source={source}
            authorType="visitor"
            handleError={handleAttachmentError}
            align="start"
          />
        );
      } else {
        attachmentChild = (
          <Attachment
            attachment={visitorAttachments[0]}
            source={source}
            authorType="visitor"
            handleError={handleAttachmentError}
            standalone
          />
        );
      }

      return (
        <div className="mt-1 flex flex-col items-start gap-2">
          {message.text && (
            <div
              className={cn(
                'w-fit max-w-lg rounded-lg bg-background px-4 py-1.75 text-sm leading-5 font-normal break-words text-foreground shadow-sm',
                VISITOR_MARKDOWN_CLASSES,
                group && 'rounded-bl-none'
              )}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {attachmentChild}
        </div>
      );
    }

    return (
      <div className="mt-1 flex">
        <div
          className={cn(
            'flex w-fit max-w-lg flex-col rounded-lg bg-background px-4 py-1.75 break-words shadow-sm',
            group && 'rounded-bl-none'
          )}
        >
          {hasAttachments && (
            <div className={message?.text ? 'mb-2' : ''}>
              {splitArray(visitorAttachments, 4).map((chunk, chunkIndex) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={chunkIndex}
                  className={cn(message?.text && 'flex justify-center')}
                >
                  <Attachments>
                    {chunk.map((attachment) => (
                      <Attachment
                        key={attachment?.id ?? attachment.url}
                        attachment={attachment}
                        source={source}
                        isSingleItem={visitorAttachments.length === 1}
                        authorType="visitor"
                        handleError={handleAttachmentError}
                      />
                    ))}
                  </Attachments>
                </div>
              ))}
            </div>
          )}{' '}
          {message.body?.type === 'audio' && message?.text?.length > 0 && (
            <span className="flex items-center gap-1">
              <FileVolumeIcon size={16} className="text-muted-foreground" />
              <span className="py-1 text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.audio_message')}
              </span>
            </span>
          )}
          {message.body?.type === 'whatsapp_flow' && (
            <span className="flex items-center gap-2">
              <WorkflowIcon size={16} className="text-muted-foreground" />
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.flow_completed')}
              </span>
            </span>
          )}
          <div
            className={cn(
              'text-sm leading-5 font-normal text-foreground',
              VISITOR_MARKDOWN_CLASSES
            )}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    );
  }

  // Container base styles for agent/brain/broadcast messages
  const containerBaseStyles =
    'relative mr-8 flex justify-end pt-1 [&_ol,&_ul]:m-0 [&_ol,&_ul]:flex [&_ol,&_ul]:flex-col [&_ol,&_ul]:gap-2 [&_ol,&_ul]:whitespace-normal [&_ol,&_ul]:pl-5 [&_ul]:list-disc [&_ol]:list-decimal';

  const withAvatarStyles =
    '[&>div:first-child]:absolute [&>div:first-child]:bottom-0 [&>div:first-child]:right-0 [&>div:first-child]:translate-x-8 [&>svg:first-child]:absolute [&>svg:first-child]:bottom-0 [&>svg:first-child]:right-0 [&>svg:first-child]:translate-x-8 [&>span:first-child]:absolute [&>span:first-child]:bottom-0 [&>span:first-child]:right-0 [&>span:first-child]:translate-x-8';

  const textWithOptionsStyles = 'flex-col items-end';

  // AGENT with attachments
  if (message.author_type === 'agent' && message?.body?.attachments?.length > 0) {
    return (
      <div
        className={cn(
          containerBaseStyles,
          showAvatar && withAvatarStyles,
          message.body?.type === 'text' && message?.body?.options && textWithOptionsStyles
        )}
      >
        <MessageAvatar showAvatar={showAvatar} message={message}>
          <InfoTooltip content={authorMember?.name} showArrow>
            <Avatar size="xs">
              <AvatarImage src={authorMember?.avatar} alt={authorMember?.name} />
              <AvatarFallback size="xs">{authorMember?.name?.[0] || '?'}</AvatarFallback>
            </Avatar>
          </InfoTooltip>
        </MessageAvatar>
        <Response
          text={message.text || ''}
          attachments={message.body.attachments}
          source={source}
          authorType={message.author_type}
          handleAttachmentError={handleAttachmentError}
        />
      </div>
    );
  }

  // BRAIN
  if (message.author_type === 'brain') {
    const { url, name } = message.body as { url?: string; name?: string };
    const fileUrl = buildStoredFileUrl(source, url, name);
    return (
      <div
        className={cn(
          containerBaseStyles,
          showAvatar && withAvatarStyles,
          message.body.type === 'text' && message?.body?.options && textWithOptionsStyles
        )}
      >
        <MessageAvatar showAvatar={showAvatar} message={message}>
          <InfoTooltip
            content={(() => {
              return `${t(`common.aiAgentCap`)} ${brain?.name}`;
            })()}
            showArrow
          >
            <ZapIcon className="text-muted-foreground" />
          </InfoTooltip>
        </MessageAvatar>
        <Response
          {...message.body}
          url={fileUrl}
          hasAvatar={showAvatar}
          handleAttachmentError={handleAttachmentError}
        />
      </div>
    );
  }

  const getTooltipTitle = () => {
    if (message.author_type === AuthorType.BROADCAST) {
      if (broadcast) {
        return `${t('main_navigation.broadcast_one')} ${broadcast?.name}`;
      }
      return t('broadcasts.deleted_broadcast');
    }
    if (message.author_type === AuthorType.CAMPAIGN) {
      if (campaign) {
        return `${t('common.campaign')} ${campaign?.name}`;
      }
      return t('campaigns.deleted_campaign');
    }
    return '';
  };
  // BROADCAST
  if (message.author_type === AuthorType.BROADCAST || message.author_type === AuthorType.CAMPAIGN) {
    const icon =
      message.author_type === AuthorType.BROADCAST ? (
        <MegaphoneIcon className="text-muted-foreground" />
      ) : (
        <GoalIcon className="text-muted-foreground" />
      );
    return (
      <div className={cn(containerBaseStyles, 'flex-col', showAvatar && withAvatarStyles)}>
        <MessageAvatar showAvatar={showAvatar} message={message}>
          <InfoTooltip
            content={getTooltipTitle()}
            open={getTooltipTitle() ? undefined : false}
            showArrow
          >
            {icon}
          </InfoTooltip>
        </MessageAvatar>
        <Response
          action_id={message.message_id}
          type="text"
          text={message?.text}
          hasAvatar={showAvatar}
          handleAttachmentError={handleAttachmentError}
          {...message.body}
        />
      </div>
    );
  }

  // INTEGRATION (WhatsApp Business App coexistence messages)
  if (message.author_type === AuthorType.INTEGRATION) {
    return (
      <div className={cn(containerBaseStyles, showAvatar && withAvatarStyles)}>
        <MessageAvatar showAvatar={showAvatar} message={message}>
          <InfoTooltip content={t('conversation.whatsapp_business_app')} showArrow>
            <SmartphoneIcon className="text-muted-foreground" />
          </InfoTooltip>
        </MessageAvatar>
        <Response
          action_id={message.message_id}
          type="text"
          text={message?.text}
          hasAvatar={showAvatar}
          source={source}
          handleAttachmentError={handleAttachmentError}
          {...message.body}
        />
      </div>
    );
  }

  // TEMPLATES
  if ((message.body?.type as string) === 'template') {
    const options = message?.body?.template?.components
      ?.find((component) => component.type.toLowerCase() === 'buttons')
      ?.buttons?.map((option) => ({ text: option.text, label: option.text }));

    return (
      <div
        className={cn(
          containerBaseStyles,
          showAvatar && withAvatarStyles,
          options && textWithOptionsStyles
        )}
      >
        <MessageAvatar showAvatar={showAvatar} message={message}>
          <InfoTooltip content={authorMember?.name} showArrow>
            <Avatar size="xs">
              <AvatarImage src={authorMember?.avatar} alt={authorMember?.name} />
              <AvatarFallback size="xs">{authorMember?.name?.[0] || '?'}</AvatarFallback>
            </Avatar>
          </InfoTooltip>
        </MessageAvatar>
        <Response
          action_id={message.message_id}
          type="text"
          text={message?.text}
          hasAvatar={showAvatar}
          source={source}
          options={options}
          handleAttachmentError={handleAttachmentError}
        />
      </div>
    );
  }

  // FALLBACK (includes older agent messages without attachments)
  const hasBody = Boolean(message.body);
  const fallbackResponseProps: ComponentProps<typeof Response> = hasBody
    ? { text: message?.text, ...message.body, hasAvatar: showAvatar, source, handleAttachmentError }
    : {
        action_id: message.message_id,
        type: 'text',
        text: message?.text,
        hasAvatar: showAvatar,
        source,
        handleAttachmentError,
      };

  return (
    <div
      className={cn(
        containerBaseStyles,
        showAvatar && withAvatarStyles,
        message.body?.type === 'text' && message?.body?.options && textWithOptionsStyles
      )}
    >
      <MessageAvatar showAvatar={showAvatar} message={message}>
        <InfoTooltip content={authorMember?.name} showArrow>
          <Avatar size="xs">
            <AvatarImage src={authorMember?.avatar} alt={authorMember?.name} />
            <AvatarFallback size="xs">{authorMember?.name?.[0] || '?'}</AvatarFallback>
          </Avatar>
        </InfoTooltip>
      </MessageAvatar>
      <Response {...fallbackResponseProps} />
    </div>
  );
};

export default memo(Message);
